import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Input, Select, Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * Appeal workflow modals.
 *
 * - FileAppealModal: POST /api/workflow/file-appeal/:parcelId
 *   Sets under_appeal=true; updates appeal_date; flips matching
 *   parcel_value status to 'appealed'; logs audit.
 *
 * - RecordOutcomeModal: POST /api/workflow/appeal-outcome/:parcelId
 *   For outcome='lowered': inserts settled parcel_value; updates
 *   parcel snapshot; auto-creates contingency invoice line item per
 *   business rules. For other outcomes: closes the appeal cleanly.
 *
 * Phase 11 Block 10 PR 1 (2026-05-12).
 */

interface FileAppealModalProps {
  open: boolean;
  onClose: () => void;
  parcelId: string;
  parcelLabel: string;            // human label for the title ("Parcel 1005301")
}

export function FileAppealModal({ open, onClose, parcelId, parcelLabel }: FileAppealModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [appealDate, setAppealDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/workflow/file-appeal/${parcelId}`, {
      appeal_date: appealDate || null,
      note: note || null,
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: 'Appeal filed.' });
      queryClient.invalidateQueries({ queryKey: ['parcel', parcelId] });
      queryClient.invalidateQueries({ queryKey: ['parcels-list'] });
      queryClient.invalidateQueries({ queryKey: ['parcel-values', parcelId] });
      // The filed appeal advances the canonical appeal record — refresh the
      // parcel's appeal-progress timeline and the appeals pipeline (bare
      // ['appeals-queue'] prefix matches every ['appeals-queue', kind]).
      queryClient.invalidateQueries({ queryKey: ['appeals-by-parcel', parcelId] });
      queryClient.invalidateQueries({ queryKey: ['appeals-queue'] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={`File appeal: ${parcelLabel}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            File appeal
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={{
            display: 'block',
            marginBottom: 'var(--sp-1)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--color-ink)',
          }}>Appeal date</span>
          <Input
            type="date"
            value={appealDate}
            onChange={e => setAppealDate(e.target.value)}
            required
          />
        </label>
        <label style={{ display: 'block' }}>
          <span style={{
            display: 'block',
            marginBottom: 'var(--sp-1)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--color-ink)',
          }}>Note (optional)</span>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: 'var(--sp-2) var(--sp-3)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)',
              background: 'var(--color-card)',
              color: 'var(--color-ink)',
              fontSize: 'var(--fs-md)',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
            placeholder="Context for the audit log"
          />
        </label>
      </form>
    </Modal>
  );
}

interface RecordOutcomeModalProps {
  open: boolean;
  onClose: () => void;
  parcelId: string;
  parcelLabel: string;
  proposedValue: number | null;
}

type Outcome = 'lowered' | 'no_change' | 'withdrawn';

export function RecordOutcomeModal({ open, onClose, parcelId, parcelLabel, proposedValue }: RecordOutcomeModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [outcome, setOutcome] = useState<Outcome>('lowered');
  const [settledValue, setSettledValue] = useState('');
  const [note, setNote] = useState('');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/workflow/appeal-outcome/${parcelId}`, {
      outcome,
      settled_value: outcome === 'lowered' ? Number(settledValue) : null,
      note: note || null,
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: `Appeal outcome recorded: ${outcome}.` });
      queryClient.invalidateQueries({ queryKey: ['parcel', parcelId] });
      queryClient.invalidateQueries({ queryKey: ['parcels-list'] });
      queryClient.invalidateQueries({ queryKey: ['parcel-values', parcelId] });
      // Recording the outcome advances the canonical appeal record — refresh the
      // parcel's appeal-progress timeline and the appeals pipeline (bare
      // ['appeals-queue'] prefix matches every ['appeals-queue', kind]).
      queryClient.invalidateQueries({ queryKey: ['appeals-by-parcel', parcelId] });
      queryClient.invalidateQueries({ queryKey: ['appeals-queue'] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const canSubmit = outcome !== 'lowered' || (settledValue.trim() !== '' && !isNaN(Number(settledValue)));

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={`Record appeal outcome: ${parcelLabel}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Record outcome
          </Button>
        </>
      }
    >
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{
          display: 'block',
          marginBottom: 'var(--sp-1)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-medium)',
          color: 'var(--color-ink)',
        }}>Outcome</span>
        <Select
          value={outcome}
          onChange={e => setOutcome(e.target.value as Outcome)}
          options={[
            { value: 'lowered', label: 'Lowered (settled at a reduced value)' },
            { value: 'no_change', label: 'No change' },
            { value: 'withdrawn', label: 'Withdrawn' },
          ]}
        />
      </label>

      {outcome === 'lowered' && (
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={{
            display: 'block',
            marginBottom: 'var(--sp-1)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--color-ink)',
          }}>Settled market value ($)</span>
          <Input
            type="number"
            value={settledValue}
            onChange={e => setSettledValue(e.target.value)}
            placeholder="e.g. 4500000"
            required
          />
          {proposedValue != null && settledValue && !isNaN(Number(settledValue)) && (
            <div style={{
              marginTop: 'var(--sp-1)',
              fontSize: 'var(--fs-sm)',
              color: 'var(--color-ink-3)',
            }}>
              Reduction: ${(proposedValue - Number(settledValue)).toLocaleString()} (
              {proposedValue > 0 ? Math.round(((proposedValue - Number(settledValue)) / proposedValue) * 100) : 0}% off proposed)
            </div>
          )}
        </label>
      )}

      <label style={{ display: 'block' }}>
        <span style={{
          display: 'block',
          marginBottom: 'var(--sp-1)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-medium)',
          color: 'var(--color-ink)',
        }}>Note (optional)</span>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          style={{
            width: '100%',
            padding: 'var(--sp-2) var(--sp-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            background: 'var(--color-card)',
            color: 'var(--color-ink)',
            fontSize: 'var(--fs-md)',
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none',
          }}
          placeholder="Context for the audit log"
        />
      </label>
    </Modal>
  );
}
